import React from "react";
import { Link } from "react-router-dom";

import Loader from "shared-anpost-components/components/loader/Loader";
import Notification from "shared-anpost-components/components/notification";
import Flyout from "shared-anpost-components/components/flyout";
import { ReactComponent as ArrowLeftSvg } from "shared-anpost-components/images/left-arrow.svg";

import useOverlay, { OVERLAY_COMPONENTS } from "../../../../../hooks/useOverlay";
import { getCustomerDataItem, getFileBlob, getFiles, getOrder, addCustomerDataItemToOrder } from "../../../../../services/Api";
import { DMOrderItemCard } from "./DmOrderItemCard";
import UploadDatabase from "./UploadDatabaseOverlay";
import { Box, Text } from "../../ui";
import { STATUS, parseOrderItem, constructImagePath, useSearchParams } from "../utils";
import { DmTransactionCard } from "./DmOrderList";

export function DmOrderDetails() {
    const { getSearchParam } = useSearchParams();
    const orderId = getSearchParam("orderId");
    const [data, setData] = React.useState(undefined);
    const [error, setError] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(true);
    const [itemToOrderAgain, setItemToOrderAgain] = React.useState(null); // order item to order again
    const [orderAgainError, setOrderAgainError] = React.useState(null);

    React.useLayoutEffect(() => {
        removeLeftMargin();
    }, []);

    const { isOverlayOpen, overlayComponent, toggleOverlay, closeOverlayBn } = useOverlay();

    const userProfile = React.useMemo(() => {
        return JSON.parse(window.sessionStorage.getItem("userProfile") || "{}");
    }, []);

    React.useEffect(() => {
        if (!orderId || !userProfile?.uid) return;
        (async () => {
            setIsLoading(true);
            const res = await getOrderItemsData(orderId, userProfile?.uid);
            if (res.error) setError(res.error);
            if (res.data) setData(res.data);
            setIsLoading(false);
        })();
    }, [orderId, userProfile?.uid]);

    const handleOrderItemAgain = async (customerDataItem, isUsingCustomDatabase = false, customDatabaseFileId = null) => {
        setOrderAgainError(null);
        setIsLoading(true);
        const res = await addCustomerDataItemToOrder(customerDataItem, isUsingCustomDatabase, customDatabaseFileId);
        if (res.error) setOrderAgainError(res.error);
        setIsLoading(false);
        if (res.data) {
            setOrderAgainError(null);
            window.location.href = `${window.origin}/Commerce/Direct-Mail/Basket`;
        }
    };

    const notification = React.useMemo(() => {
        if (error) return { color: "red", text: error };
        if (data?.Status?.Name === STATUS.PRE_ACKNOWLEDGED) {
            return { color: "blue", text: "Your order is pending." };
        }
        if (data?.Status?.Name === STATUS.CANCELLED) {
            return { color: "red", text: "Your order was cancelled." };
        }
    }, [error, data]);

    if (isLoading || !orderId) return <Loader />;

    return (
        <>
            <Box minHeight="100vh" padding={["4rem 1.6rem", "8rem 1.6rem"]} width="100%">
                <Box width="100%" direction="column" alignItems="center">
                    <Box maxWidth="117rem" width="100%" gap={["2.4rem", "3.2rem"]}>
                        <Box>
                            <Box gap={["2.4rem", "4.8rem"]}>
                                <Link to="/Transactions?product=direct-mail">
                                    <Text as="span" alignItems="center" display="flex" gap="8px" fontWeight={700} color="#008755" fontSize={["14px", "16px"]}>
                                        <ArrowLeftSvg /> Back to Transaction history
                                    </Text>
                                </Link>
                                {orderAgainError && <Notification colour="red" text="An error occurred" additionalText="There was a problem with ordering item again" />}
                                <h3>Order details</h3>
                            </Box>
                            <Box flexDirection="row" justifyContent={["flex-start", "flex-end"]}>
                                <Text fontSize="12px">Prices displayed exclude VAT</Text>
                            </Box>
                        </Box>
                        <Box gap="2.4rem">
                            {data && (
                                <DmTransactionCard
                                    OrderDate={data.OrderDate}
                                    OrderNumber={data.OrderNumber}
                                    Status={data.Status}
                                    TotalPriceExVat={data.TotalPrice.TotalPriceExVat}
                                    OrderItems={data.OrderItems}
                                    canNavigate={false}
                                    showOverview={false}
                                />
                            )}
                            {notification && <Notification colour={notification.color} text={notification.text} />}
                            {data?.OrderItems?.map(it => {
                                const { name, price, areas, addresses, deliveryDate, image, status, hasFreeCopy, additionalCopies } = parseOrderItem(it);
                                return (
                                    <DMOrderItemCard
                                        orderStatus={data.Status?.Name}
                                        key={it.Id}
                                        customerDataItem={it.CustomerDataItem}
                                        image={image}
                                        name={name}
                                        price={price}
                                        areas={areas}
                                        addresses={addresses}
                                        deliveryDate={deliveryDate}
                                        status={status}
                                        hasFreeCopy={hasFreeCopy}
                                        additionalCopies={additionalCopies}
                                        handleOrderItemAgain={handleOrderItemAgain}
                                        setItemToOrderAgain={setItemToOrderAgain}
                                        toggleUploadDatabaseOverlay={event => toggleOverlay(event, OVERLAY_COMPONENTS.uploadDatabase)}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Flyout openFlyout={isOverlayOpen} closeBtn={closeOverlayBn} closeLabel="Close" closeFlyout={toggleOverlay}>
                <div>
                    {overlayComponent === OVERLAY_COMPONENTS.uploadDatabase && (
                        <UploadDatabase closeOverlay={toggleOverlay} itemToOrderAgain={itemToOrderAgain} handleOrderItemAgain={handleOrderItemAgain} />
                    )}
                </div>
            </Flyout>
        </>
    );
}

const errorMessage = "Failed to load order details.";

async function getOrderItemsData(orderId, uId) {
    try {
        const orderResponse = await getOrder(orderId); // basic order data
        if (orderResponse.error) return { error: errorMessage, data: null };

        const customerDataPromises = orderResponse.data.Data.OrderItems.map(it => getCustomerDataItem(it.CustomerDataItem.Id)); // additional order details
        const filesPathResponse = await getFiles(uId);

        const customerDataResolved = await Promise.allSettled(customerDataPromises);
        const hasErrors = customerDataResolved.some(it => it.status === "rejected");

        if (hasErrors) {
            return { error: errorMessage, data: null };
        }

        const orderItemsWithImages = await Promise.all(
            orderResponse.data.Data.OrderItems.map((orderItem, i) => {
                const customerDataResult = customerDataResolved[i];
                if (customerDataResult.status !== "fulfilled") {
                    throw new Error(`Failed to load customer data for item ID: ${orderItem.CustomerDataItem.Id}`);
                }

                const imageFileItem = filesPathResponse.data.items.find(f => {
                    const [campaignId, productId] = [orderItem.CustomerDataItem.Id, orderItem.CustomerDataItem.Product.Id];
                    const bookId = `${campaignId}-${productId}`;
                    if (bookId === f?.bookId) return true;
                    return false;
                });

                if (!imageFileItem) {
                    return {
                        ...orderItem,
                        CustomerDataItem: {
                            ...orderItem.CustomerDataItem,
                            ...customerDataResult.value.data.Data,
                            Image: ""
                        }
                    };
                }

                const imagePath = constructImagePath({
                    uId,
                    bookId: imageFileItem.bookId,
                    file: imageFileItem?.imagePagesId?.[0]
                });
                // order with all the necessary details and image
                return getFileBlob({ path: imagePath, uId })
                    .then(res => URL.createObjectURL(res.data))
                    .then(imageUrl => ({
                        ...orderItem,
                        CustomerDataItem: {
                            ...orderItem.CustomerDataItem,
                            ...customerDataResult.value.data.Data,
                            Image: imageUrl
                        }
                    }));
            })
        );

        return {
            error: null,
            data: {
                ...orderResponse.data.Data,
                OrderItems: constructOrderItems(orderItemsWithImages)
            }
        };
    } catch (error) {
        console.error(error);
        return { error: errorMessage, data: null };
    }
}

/**
 * Construct order items with additional copies
 * @param {Array<Record<string, any>>} orderItems
 * @returns {Array<Record<string, any>>}
 */
const constructOrderItems = (orderItems = []) => {
    const orderItemsWithDetails = orderItems?.reduce((acc, orderItem) => {
        const { CustomerDataItem } = orderItem;

        if (!CustomerDataItem || Object.keys(CustomerDataItem).length === 0) return acc;

        if (!CustomerDataItem?.ParentCustomerDataItemId) {
            acc[CustomerDataItem.Id] = {
                ...orderItem,
                CustomerDataItem: {
                    ...CustomerDataItem,
                    AdditionalCopies: {}
                }
            };
            return acc;
        }

        const parentOrderItem = acc[CustomerDataItem.ParentCustomerDataItemId];

        // Add additional copies (a separate order item) to the parent order item - combine them
        acc[CustomerDataItem.ParentCustomerDataItemId] = {
            ...parentOrderItem,
            CustomerDataItem: {
                ...parentOrderItem.CustomerDataItem,
                AdditionalCopies: orderItem // Additional copies is a new order item, related to the parent order item
            }
        };

        return acc;
    }, {});

    // sort order items by created date, newest first
    return Object.values(orderItemsWithDetails).sort((a, b) => new Date(b.CustomerDataItem.CreatedDate) - new Date(a.CustomerDataItem.CreatedDate));
};

function removeLeftMargin() {
    const main = document.querySelector("#main");
    const footer = document.querySelector("#site-footer");
    if (main) main.style.marginLeft = "0";
    if (footer) footer.style.marginLeft = "0";
}
